import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { AuthService, Country, Region, User, UserAddressService } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CustomTicketsService } from '../../../../spartacus/custom/cms-components/myaccount/custom-tickets/services/custom-tickets.service';
import { StateWithTickets } from '../../../../spartacus/custom/cms-components/myaccount/custom-tickets/store/custom-tickets-state';
import { getTicketstListState } from '../../../../spartacus/custom/cms-components/myaccount/custom-tickets/store/selectors/feature-selector';
import { CustomValidators } from '../../../../spartacus/custom/shared/utils/validators/custom-validators';
import { RecaptchaService } from '../../../../spartacus/features/recaptcha/recaptcha.service';
import { ICON_TYPE } from '../../../icons/icon.model';
import { AttentionCompany, AttentionCompanySubjectType } from './custom-care-company.model';
import { CustomAttentionCompanyService } from './custom-care-company.service';

@Component({
  selector: 'app-custom-care-company',
  templateUrl: './custom-care-company.component.html',
  styleUrls: ['./custom-care-company.component.scss'],
  inputs: ['email', 'fullName', 'privacyPolicy', 'message', 'phone', 'companyName'],
  standalone: false
})
export class CustomCareCompanyComponent implements OnInit, OnDestroy {
  iconTypes = ICON_TYPE;
  protected subscriptions: Subscription = new Subscription();
  private platformId = inject(PLATFORM_ID);
  protected submitted = new BehaviorSubject<boolean>(false);
  public submitted$ = this.submitted.asObservable();

  @ViewChild('frm') frm?: ElementRef;

  isFormVisible = false;
  professional = false;
  user$?: Observable<User | undefined>;
  categories$?: Observable<StateWithTickets>;
  regions$?: Observable<Region[]>;
  isRegionVisible = false;
  subjectType?: AttentionCompanySubjectType;
  validUser = false;

  attentionCompany?: AttentionCompany;
  attentionCompanyForm?: UntypedFormGroup;

  constructor(
    protected fb: UntypedFormBuilder,
    private auth: AuthService,
    private userAccount: UserAccountFacade,
    protected customTicketsService: CustomTicketsService,
    protected store: Store<StateWithTickets>,
    protected customAttentionCompanyService: CustomAttentionCompanyService,
    protected userAddressService: UserAddressService,
    protected recaptchaService: RecaptchaService
  ) {}

  ngOnInit(): void {
    this.attentionCompanyForm = this.fb.group({
      attentionCompanyType: ['', Validators.required],
      email: ['', [Validators.required, CustomValidators.emailValidator]],
      fullName: ['', Validators.required],
      privacyPolicy: [false, Validators.requiredTrue],
      message: ['', Validators.required],
      phone: ['', [Validators.required, CustomValidators.phoneValidator]],
      companyName: ['', Validators.required]
    });

    this.user$ = this.auth.isUserLoggedIn().pipe(
      switchMap((isUserLoggedIn) => {
        if (isUserLoggedIn) {
          return this.userAccount.get();
        } else {
          return of(undefined);
        }
      })
    );

    this.categories$ = this.store.pipe(select(getTicketstListState));

    this.customTicketsService.loadTicketsCompanyCategory();

    this.regions$ = this.userAddressService.getRegions('ES').pipe(
      map((countries: Country[]) => {
        return countries;
      })
    );
  }

  submitForm(): void {
    this.switchToSubmitted();
    if (this.attentionCompanyForm?.valid) {
      this.recaptchaService
        .getToken()
        .then((token) => {
          if (token) {
            this.subscriptions.add(
              this.recaptchaService.validateRecaptchaToken(token).subscribe((response) => {
                if (response?.status === 200) {
                  this.attentionCompany = this.attentionCompanyForm?.value;
                  this.customAttentionCompanyService.subscribeRequest(this.attentionCompany!, this.frm!.nativeElement);
                } else {
                  this.recaptchaService.recaptchaError('forms.recaptcha.invalidToken');
                }
              })
            );
          } else {
            this.recaptchaService.recaptchaError('forms.recaptcha.invalidToken');
          }
        })
        .catch(() => this.recaptchaService.recaptchaError(null));
    } else {
      this.attentionCompanyForm?.markAllAsTouched();
    }
  }

  changeSubjectType(event: Event): void {
    this.isFormVisible = true;
    this.subjectType = AttentionCompanySubjectType[(event.target as HTMLInputElement).value as keyof typeof AttentionCompanySubjectType];
    this.checkRegionVisible();
  }

  checkRegionVisible(): void {
    this.isRegionVisible = this.subjectType === AttentionCompanySubjectType.QT_ATTENTION;
    this.attentionCompanyForm?.setControl('region', this.fb.control('', this.isRegionVisible ? [Validators.required] : [Validators.nullValidator]));
    this.attentionCompanyForm?.updateValueAndValidity();
  }

  switchToSubmitted() {
    this.submitted.next(true);
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.submitted.next(false);
      }, 1500);
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
